/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Constructor type for a `@tivocorp/data-domain-lib` service (or any
 * single-arg-constructor class) that accepts a typed params object.
 */
type ServiceCtor<S, P> = new (params: P) => S;

/**
 * Lightweight service-locator factory for interactor → domain-lib service
 * instantiation.
 *
 * Responsibilities:
 * 1. Replace bare `new XxxService(params)` calls inside interactors with a
 *    single seam, so tests can swap implementations without `jest.mock`
 *    of `@tivocorp/data-domain-lib`.
 * 2. Stay stateless in production — `getInstance` is just `new Ctor(params)`
 *    unless a test override has been registered.
 * 3. Provide explicit test-only registration / reset hooks (prefixed with
 *    `__` to discourage production use). Tests must call
 *    `__resetOverridesForTest()` in `afterEach` to avoid cross-test leak.
 *
 * Non-goals (deliberately out of scope):
 * - No singleton caching of service instances. Interactors stay stateless.
 * - No DI container, no decorators, no metadata reflection.
 * - No async resolution. Endpoint/TSRI resolution stays on `BaseInteractor`.
 */
export class ServiceLocator {
  private static readonly mOverrides: Map<unknown, unknown> = new Map<
    unknown,
    unknown
  >();

  /**
   * Returns a fresh service instance constructed with `params`, unless a
   * test override has been registered for `Ctor` — in which case the
   * registered instance is returned (and `params` is ignored).
   *
   * @param Ctor   - Service class constructor
   * @param params - Constructor parameters (typically TServicesParams)
   */
  public static getInstance<S, P>(Ctor: ServiceCtor<S, P>, params: P): S {
    const override: unknown = ServiceLocator.mOverrides.get(Ctor as unknown);
    if (override !== undefined && override !== null) {
      return override as S;
    }
    return new Ctor(params);
  }

  /**
   * Test-only: register an instance to be returned in place of `new Ctor(...)`.
   * Production code must never call this.
   */
  public static __setOverrideForTest<S, P>(
    Ctor: ServiceCtor<S, P>,
    instance: S
  ): void {
    ServiceLocator.mOverrides.set(Ctor as unknown, instance);
  }

  /**
   * Test-only: clear all registered overrides. Call in `afterEach`.
   */
  public static __resetOverridesForTest(): void {
    ServiceLocator.mOverrides.clear();
  }
}
